// Library Methods
import { useCallback, useMemo } from "react";

// Components
import Skeleton from "@mui/material/Skeleton";
import Square from "src/assets/shapes/Square/Square";
import Dash from "src/assets/shapes/Dash/Dash";
import ColoredCircle from "src/assets/shapes/ColoredCircle/ColoredCircle";
import Circle from "src/assets/shapes/Circle/Circle";
import ChartCross from "src/assets/shapes/ChartCross/ChartCross";
import GraphRings from "src/assets/shapes/GraphRings/GraphRings";

// Types
import { ChartSpotType } from "src/services/exams/types";

const DEFAULT_CONTAINER_SIZE = 624;

type EstermanChartType = {
  estermanSpots: Array<ChartSpotType>;
  containerSize?: number;
  isLiveUpdate?: boolean;
};

const EstermanChart = ({
  estermanSpots,
  containerSize = DEFAULT_CONTAINER_SIZE,
  isLiveUpdate = false,
}: EstermanChartType) => {
  // Add these values as params if they can change in the future
  const gridType = useMemo(() => "G_120", []);
  const tickNum = useMemo(() => 8, []);

  const size = useMemo(() => containerSize * 0.7, [containerSize]);
  const tickSize = useMemo(() => size / 104, [size]);
  const scaleFactor = useMemo(() => size / DEFAULT_CONTAINER_SIZE, [size]);

  const calculateXPaddingEstermanChart = useCallback(
    (xFloor: number) => {
      // jumpAxis lets us jump from negative x axis to positive x axis (half of total width + line width + padding)
      const jumpAxis = 311 * scaleFactor;
      const paddingPerUnit = 3.8 * scaleFactor;

      if (xFloor <= 0) {
        // Adjust fixed value and multiplication by the scaleFactor
        return 305 * scaleFactor - xFloor * -paddingPerUnit;
      } else {
        return xFloor * paddingPerUnit + jumpAxis;
      }
    },
    [scaleFactor]
  );

  const calculateYPaddingEstermanChart = useCallback(
    (yFloor: number) => {
      // jumpAxis lets us jump from negative y axis to negative y axis (half of total width + line width + padding)
      const jumpAxis = 311 * scaleFactor;
      const paddingPerUnit = 3.8 * scaleFactor;

      if (yFloor >= 0) {
        // Adjust fixed value and multiplication by the scaleFactor
        return 306 * scaleFactor - yFloor * paddingPerUnit;
      } else {
        return jumpAxis + yFloor * -paddingPerUnit;
      }
    },
    [scaleFactor]
  );

  // Show loading skeleton if data is not yet processed
  if (estermanSpots?.length === 0) {
    return (
      <Skeleton
        variant="rounded"
        width="100%"
        height={DEFAULT_CONTAINER_SIZE}
        sx={{
          borderRadius: "4px",
        }}
      />
    );
  }

  return (
    <main
      style={{
        height: `${size}px`,
        width: `${size}px`,
        position: "relative",
      }}
    >
      <GraphRings size={size} tickNum={tickNum} ariaLabel="esterman-ring" />

      <ChartCross
        height={size}
        width={size}
        tickNum={tickNum}
        tickSize={tickSize}
        gridType={gridType}
      />

      {/* Unseen spots */}
      {estermanSpots
        ?.filter((spot) => !spot?.seen && spot?.completed)
        ?.map((spot, i: number) => {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: calculateXPaddingEstermanChart(spot?.x),
                paddingTop: calculateYPaddingEstermanChart(spot?.y),
              }}
            >
              <Square size={scaleFactor * 8} />
            </div>
          );
        })}

      {/* Seen spots */}
      {estermanSpots
        ?.filter((spot) => spot?.seen)
        ?.map((spot, i: number) => {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: calculateXPaddingEstermanChart(spot?.x),
                paddingTop: calculateYPaddingEstermanChart(spot?.y),
              }}
            >
              <Circle size={scaleFactor * 10} />
            </div>
          );
        })}

      {/* In progress spots (live update modal) */}
      {/* Display an orange circle by default or a green circle on the "current spot" */}
      {estermanSpots
        ?.filter((spot) => !spot?.completed && spot?.decibelValue) // filter uncompleted spots with a valid decibelValue
        ?.map((spot, i: number) => {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: calculateXPaddingEstermanChart(spot?.x),
                paddingTop: calculateYPaddingEstermanChart(spot?.y),
              }}
            >
              {!isLiveUpdate ? (
                <Dash size={scaleFactor * 8} />
              ) : (
                <ColoredCircle
                  isOrange={!spot?.current}
                  size={scaleFactor * 8}
                />
              )}
            </div>
          );
        })}
    </main>
  );
};

export default EstermanChart;
